import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtCurrency, fmtDate, packagePaymentAmounts } from "@/lib/utils-app";
import { Search, Trash2, CreditCard, DollarSign, X } from "lucide-react";
import SubscriberAvatar from "@/components/SubscriberAvatar";
import { openMercadoPagoCheckout, pollPaymentStatus } from "@/lib/mercadoPagoCheckout";

const defaultPayForm = () => ({
  amount: 0,
  method: "cash",
  payment_date: new Date().toISOString().slice(0, 10),
  period_days: 30,
  notes: "",
  type: "monthly",
});

export default function AdminPayments() {
  const [items, setItems] = useState([]);
  const [subs, setSubs] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [onlineAvailable, setOnlineAvailable] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [payForm, setPayForm] = useState(defaultPayForm());
  const stopPollRef = useRef(null);

  const loadPayments = () => api.get("/admin/payments").then((r) => setItems(r.data || []));
  const loadSubs = () => api.get("/admin/subscribers").then((r) => setSubs(r.data || []));
  const refresh = () => Promise.all([loadPayments(), loadSubs()]);

  useEffect(() => {
    refresh();
    api.get("/admin/settings").then((r) => {
      const s = r.data || {};
      setOnlineAvailable(!!s.online_payments_licensed && !!s.mp_enabled);
    });
    return () => stopPollRef.current?.();
  }, []);

  const payAmounts = packagePaymentAmounts(payTarget?.package, payForm.type);

  useEffect(() => {
    if (!payOpen || payForm.method === "online") return;
    setPayForm((f) => ({ ...f, amount: payAmounts.total }));
  }, [payOpen, payForm.type, payForm.method, payAmounts.total]);

  const openPayModal = (subscriber) => {
    if (!subscriber?.package_id && !subscriber?.package) {
      toast.error("El suscriptor no tiene paquete asignado");
      return;
    }
    const monthly = Number(subscriber.package?.monthly_cost || 0);
    setPayTarget(subscriber);
    setPayForm({
      ...defaultPayForm(),
      amount: monthly,
      period_days: Number(subscriber.package?.duration_days || 30) || 30,
      type: "monthly",
    });
    setPayOpen(true);
  };

  const addPayment = async () => {
    if (!payTarget?.id) return;
    try {
      if (payForm.method === "online") {
        const { data } = await api.post("/admin/payments/checkout", {
          subscriber_id: payTarget.id,
          type: payForm.type,
          notes: payForm.notes,
          origin_url: window.location.origin,
        });
        if (!data?.checkout_url || !data?.session_id) {
          toast.error("No se pudo crear la sesión de pago");
          return;
        }
        const opened = openMercadoPagoCheckout(data.checkout_url, data.session_id);
        if (opened) {
          toast.info("Mercado Pago en otra pestaña. Al pagar, el registro aparecerá en el historial.");
        } else {
          toast.warning("Abre el enlace de pago en una nueva pestaña.");
          window.open(data.checkout_url, "_blank");
        }
        setPayOpen(false);
        stopPollRef.current?.();
        stopPollRef.current = pollPaymentStatus(
          (sid) => api.get(`/payments/checkout/status/${sid}`).then((r) => r.data),
          data.session_id,
          {
            onPaid: () => {
              toast.success("Pago en línea confirmado");
              refresh();
            },
            onFailed: () => toast.error("El pago no se completó"),
            onTimeout: () => toast.info("Si ya pagó, recarga la página."),
          },
        );
        return;
      }
      const body = {
        ...payForm,
        subscriber_id: payTarget.id,
        amount: Number(payForm.amount),
        period_days: Number(payForm.period_days),
        payment_date: new Date(payForm.payment_date).toISOString(),
      };
      await api.post("/admin/payments", body);
      toast.success(
        payForm.method === "transfer"
          ? "Transferencia registrada (pendiente de confirmación)"
          : "Mensualidad registrada",
      );
      setPayOpen(false);
      setPayTarget(null);
      setPayForm(defaultPayForm());
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo registrar el pago");
    }
  };

  const confirmTransfer = async (id) => {
    try {
      await api.post(`/admin/payments/${id}/confirm`);
      toast.success("Transferencia confirmada");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar pago?")) return;
    await api.delete(`/admin/payments/${id}`);
    toast.success("Eliminado");
    refresh();
  };

  const statusClass = (s) =>
    ({ valid: "badge-valid", expiring_soon: "badge-soon", overdue: "badge-overdue" }[s] ||
    "bg-white/5 text-white/40 border border-white/10");
  const statusLabel = (s) =>
    ({ valid: "Al día", expiring_soon: "Por vencer", overdue: "Vencido", unknown: "Sin pagos" }[s] || s);

  const filteredSubs = subs.filter((s) => {
    if (filter !== "all" && s.payment_status !== filter) return false;
    return s.full_name.toLowerCase().includes(q.toLowerCase());
  });

  return (
    <div data-testid="admin-payments-page">
      <div className="text-xs uppercase tracking-[0.3em] text-[#E63946]">Membresías</div>
      <h1 className="font-display text-4xl text-white mt-1">Pagos & estados</h1>

      <div className="mt-6 grid lg:grid-cols-3 gap-4">
        <div className="glass p-4 rounded-sm border-l-4 border-green-500">
          <div className="text-xs uppercase text-white/40">Al día</div>
          <div className="font-display text-3xl text-green-400">
            {subs.filter((s) => s.payment_status === "valid").length}
          </div>
        </div>
        <div className="glass p-4 rounded-sm border-l-4 border-yellow-500">
          <div className="text-xs uppercase text-white/40">Por vencer (7 días)</div>
          <div className="font-display text-3xl text-yellow-400">
            {subs.filter((s) => s.payment_status === "expiring_soon").length}
          </div>
        </div>
        <div className="glass p-4 rounded-sm border-l-4 border-red-500">
          <div className="text-xs uppercase text-white/40">Vencidos</div>
          <div className="font-display text-3xl text-red-400">
            {subs.filter((s) => s.payment_status === "overdue").length}
          </div>
        </div>
      </div>

      <h2 className="font-display text-2xl text-white mt-10">Estado por suscriptor</h2>
      <div className="mt-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={16} />
          <input
            className="input-dark pl-10"
            placeholder="Buscar atleta…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <select className="input-dark md:w-56" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="all">Todos los estados</option>
          <option value="valid">🟢 Al día</option>
          <option value="expiring_soon">🟡 Por vencer</option>
          <option value="overdue">🔴 Vencido</option>
          <option value="unknown">⚪ Sin pagos</option>
        </select>
      </div>

      <div className="mt-4 glass rounded-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-white/40 border-b border-theme-secondary/10">
              <th className="p-3">Atleta</th>
              <th className="p-3">Paquete</th>
              <th className="p-3">Último pago</th>
              <th className="p-3">Próximo vto.</th>
              <th className="p-3">Monto adeudado</th>
              <th className="p-3">Estado</th>
              <th className="p-3 text-right">Acción</th>
            </tr>
          </thead>
          <tbody>
            {filteredSubs.map((s) => (
              <tr key={s.id} className="border-b border-theme-secondary/10 hover:bg-white/[0.02]">
                <td className="p-3">
                  <div className="flex items-center gap-3 text-white">
                    <SubscriberAvatar subscriber={s} size={40} />
                    <span>{s.full_name}</span>
                  </div>
                </td>
                <td className="p-3 text-white/60">{s.package?.name || "—"}</td>
                <td className="p-3 text-white/60">{fmtDate(s.last_payment?.payment_date)}</td>
                <td className="p-3 text-white/60">{fmtDate(s.next_due_date)}</td>
                <td className="p-3 text-[#E63946]">
                  {s.package?.monthly_cost
                    ? fmtCurrency(s.package.monthly_cost)
                    : "—"}
                </td>
                <td className="p-3">
                  <span className={`badge-status ${statusClass(s.payment_status)}`}>
                    {statusLabel(s.payment_status)}
                  </span>
                </td>
                <td className="p-3 text-right">
                  <button
                    type="button"
                    onClick={() => openPayModal(s)}
                    disabled={!s.package}
                    className="btn-primary text-xs py-1.5 disabled:opacity-40"
                    data-testid={`charge-${s.id}`}
                  >
                    <DollarSign size={14} /> Cobrar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="font-display text-2xl text-white mt-10">Historial completo de pagos</h2>
      <div className="mt-4 glass rounded-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-white/40 border-b border-theme-secondary/10">
              <th className="p-3">Fecha</th>
              <th className="p-3">Atleta</th>
              <th className="p-3">Tipo</th>
              <th className="p-3">Método</th>
              <th className="p-3">Estado</th>
              <th className="p-3">Monto</th>
              <th className="p-3">Registrado por</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-b border-theme-secondary/10 text-white/60">
                <td className="p-3">{fmtDate(p.payment_date)}</td>
                <td className="p-3 text-white">{p.subscriber_name || "—"}</td>
                <td className="p-3 capitalize">{p.type}</td>
                <td className="p-3 capitalize flex items-center gap-1">
                  <CreditCard size={12} /> {p.method}
                </td>
                <td className="p-3">
                  {p.status === "pending_confirmation" ? (
                    <span className="text-amber-400 text-xs">Pendiente transferencia</span>
                  ) : (
                    <span className="text-green-400/80 text-xs">Confirmado</span>
                  )}
                </td>
                <td className="p-3 text-[#E63946] font-bold">{fmtCurrency(p.amount)}</td>
                <td className="p-3 text-white/40">{p.recorded_by}</td>
                <td className="p-3 text-right flex gap-2 justify-end">
                  {p.method === "transfer" && p.status === "pending_confirmation" && (
                    <button onClick={() => confirmTransfer(p.id)} className="btn-primary text-xs py-1">
                      Confirmar
                    </button>
                  )}
                  <button onClick={() => del(p.id)} className="text-red-400">
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center py-10 text-white/40">
                  Sin pagos
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {payOpen && payTarget && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-start justify-center p-4 overflow-y-auto"
          onClick={() => setPayOpen(false)}
        >
          <div className="glass-strong max-w-2xl w-full rounded-sm my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-theme-secondary/10">
              <div>
                <div className="font-display text-2xl text-white">Cobrar mensualidad</div>
                <div className="text-sm text-white/60 mt-1">{payTarget.full_name}</div>
              </div>
              <button type="button" onClick={() => setPayOpen(false)} className="text-white/40 hover:text-white">
                <X />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Tipo">
                  <select
                    className="input-dark mt-1"
                    value={payForm.type}
                    onChange={(e) => setPayForm({ ...payForm, type: e.target.value })}
                  >
                    <option value="monthly">Mensualidad</option>
                    <option value="inscription">Inscripción</option>
                  </select>
                </Field>
                <Field label="Método">
                  <select
                    className="input-dark mt-1"
                    value={payForm.method}
                    onChange={(e) => setPayForm({ ...payForm, method: e.target.value })}
                  >
                    <option value="cash">Efectivo</option>
                    <option value="transfer">Transferencia</option>
                    {onlineAvailable && <option value="online">En línea</option>}
                  </select>
                </Field>
                {payForm.type === "inscription" && payTarget.package && (
                  <div className="md:col-span-2 text-sm text-white/60 bg-white/5 p-3 rounded-sm space-y-1">
                    <div>
                      Inscripción:{" "}
                      {payTarget.package.inscription_free
                        ? "Gratis"
                        : fmtCurrency(payAmounts.inscription_part)}
                    </div>
                    <div>Mensualidad: {fmtCurrency(payAmounts.monthly_part)}</div>
                    <div className="text-[#FFD700] font-medium">Total: {fmtCurrency(payAmounts.total)}</div>
                  </div>
                )}
                <Field label="Monto">
                  <input
                    type="number"
                    step="0.01"
                    className="input-dark mt-1"
                    value={payForm.method === "online" ? payTarget.package?.monthly_cost || 0 : payForm.amount}
                    onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
                    disabled={payForm.method === "online"}
                  />
                </Field>
                <Field label="Fecha de pago">
                  <input
                    type="date"
                    className="input-dark mt-1"
                    value={payForm.payment_date}
                    onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })}
                  />
                </Field>
                <Field label="Periodo (días)">
                  <input
                    type="number"
                    className="input-dark mt-1"
                    value={payForm.period_days}
                    onChange={(e) => setPayForm({ ...payForm, period_days: e.target.value })}
                  />
                </Field>
              </div>
              <Field label="Notas">
                <textarea
                  rows={2}
                  className="input-dark mt-1 resize-none"
                  value={payForm.notes}
                  onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })}
                />
              </Field>
              {payForm.method === "transfer" && (
                <p className="text-xs text-amber-400/90">
                  La transferencia quedará pendiente hasta que confirmes el depósito.
                </p>
              )}
              {payForm.method === "online" && (
                <p className="text-xs text-white/60">
                  Se abrirá Mercado Pago en otra pestaña. Al acreditarse, el pago aparece en el historial.
                </p>
              )}
            </div>
            <div className="p-5 border-t border-theme-secondary/10 flex justify-end gap-3">
              <button type="button" onClick={() => setPayOpen(false)} className="btn-outline text-sm py-2">
                Cancelar
              </button>
              <button type="button" onClick={addPayment} className="btn-primary text-sm py-2">
                Registrar cobro
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block text-xs uppercase tracking-widest text-white/60">
      {label}
      {children}
    </label>
  );
}
